import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ScopeOfPracticeEnum } from '@umss/shared/utils';
import {
  FacilityBillableRateDto,
  CreateFacilityBillableRateDto,
  ScopeDifferentialRateDto
} from '../../models';

@Injectable({ providedIn: 'root' })
export class FacilityBillableRatesService {
  private readonly baseUrl = '/api/facilities';
  private readonly http = inject(HttpClient);

  // ============================================
  // ENDPOINT 8: GET /api/facilities/{id}/billable-rates
  // Get facility billable rates
  // ============================================
  getAll(facilityId: string): Observable<FacilityBillableRateDto[]> {
    return this.http.get<FacilityBillableRateDto[]>(
      `${this.baseUrl}/${facilityId}/billable-rates`
    );
  }

  get(
    facilityId: string,
    billrateId: string
  ): Observable<FacilityBillableRateDto[]> {
    return this.http.get<FacilityBillableRateDto[]>(
      `${this.baseUrl}/${facilityId}/billable-rates/${billrateId}`
    );
  }

  // ============================================
  // ENDPOINT 9: POST /api/facilities/{id}/billable-rates
  // Set facility billable rate
  // ============================================
  create(
    facilityId: string,
    body: CreateFacilityBillableRateDto
  ): Observable<FacilityBillableRateDto> {
    return this.http.post<FacilityBillableRateDto>(
      `${this.baseUrl}/${facilityId}/billable-rates`,
      body
    );
  }

  update(
    facilityId: string,
    billrateId: string,
    body: Partial<CreateFacilityBillableRateDto>
  ): Observable<FacilityBillableRateDto> {
    return this.http.put<FacilityBillableRateDto>(
      `${this.baseUrl}/${facilityId}/billable-rates/${billrateId}`,
      body
    );
  }

  delete(facilityId: string, billrateId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/${facilityId}/billable-rates/${billrateId}`
    );
  }

  // ============================================
  // ENDPOINT 10: GET /api/facilities/{id}/billable-rates/{scope}
  // Get current billable rate for a scope
  // ============================================
  getCurrentRateByScope(
    facilityId: string,
    scope: ScopeOfPracticeEnum
  ): Observable<FacilityBillableRateDto | null> {
    return this.http.get<FacilityBillableRateDto | null>(
      `${this.baseUrl}/${facilityId}/billable-rates/scope/${scope}`
    );
  }

  getDifferentialRates(
    facilityId: string
  ): Observable<ScopeDifferentialRateDto[]> {
    return this.http.get<ScopeDifferentialRateDto[]>(
      `${this.baseUrl}/${facilityId}/billable-rates/differential-rates`
    );
  }
}
